import { useCallback, useEffect, useMemo, useState } from 'react';
import constate from '../../common/lib/constate';
import { useHistory, useLocation } from 'react-router-dom';
import { INITIAL_VOICEOVER_VOLUME } from '../constants/voiceovers';
import { roundToDigit } from '../utils';
import { timeStampToMs } from '../utils/TimeUtil';
import { useVideoProgressContext } from './VideoProgressContext';

function useVoiceover() {
  const [isVoiceoverOpened, setIsVoicoverOpened] = useState(false);
  const [generalVoiceoverId, setGeneralVoiceoverId] = useState(null);

  const [voiceovers, setVoiceovers] = useState([]);
  const [initialVoiceovers, setInitialVoiceovers] = useState([]);

  const [selectedVoiceover, setSelectedVoiceover] = useState({});

  const [voiceoverSeekValue, setVoiceoverSeekValue] = useState(0);
  const [isNewVoiceoverSeekValue, setIsNewVoiceoverSeekValue] = useState(false);

  const [isVoiceoversChanged, setIsVoiceoversChanged] = useState(false);

  const [isAudioPermissionChecked, setIsAudioPermissionChecked] = useState(false);
  const [isAudioPermissionAllowed, setIsAudioPermissionAllowed] = useState(false);

  const [isVoiceoverRecording, setIsVoiceoverRecording] = useState(false);
  const [isVoiceoverCountdown, setIsVoiceoverCountdown] = useState(false);

  const [voiceoverVolume, setVoiceoverVolume] = useState(INITIAL_VOICEOVER_VOLUME);
  const [isVoiceoversLoading, setIsVoiceoversLoading] = useState(false);

  const [videoParts, setVideoParts] = useState([]);

  const history = useHistory();
  const location = useLocation();

  const { videoProgress } = useVideoProgressContext();

  const openVoiceover = useCallback(
    (pushHistory = true) => {
      if (!isVoiceoverOpened) {
        setIsVoicoverOpened(true);
        pushHistory &&
          history.push({
            pathname: location.pathname,
            search: 'voiceover=true',
          });
      }
    },
    [isVoiceoverOpened, history, location]
  );

  const closeVoiceover = useCallback((popHistory = true) => {
    setIsVoicoverOpened(false);
    if (popHistory) {
      if (history.length > 2) {
        history.goBack();
      } else {
        // Handle cases when page with search parameter was loaded
        history.replace({
          pathname: location.pathname,
          search: '',
        });
      }
    }
  }, []);

  const updateInitialVoiceovers = useCallback(values => {
    setInitialVoiceovers(values);
  }, []);

  // add new voiceovers
  const updateVoiceovers = useCallback(values => {
    setVoiceovers(values);
  }, []);

  // change existing voiceovers
  const changeVoiceovers = useCallback((id, changes) => {
    setVoiceovers(prevState =>
      prevState.map(voiceover => {
        if (voiceover.id === id) {
          return { ...voiceover, ...changes };
        }

        return voiceover;
      })
    );
  }, []);

  const changeSelectedVoiceover = useCallback(voiceover => {
    if (Object.keys(voiceover).length !== 0) {
      changeVoiceoverVolume(voiceover?.volume ?? INITIAL_VOICEOVER_VOLUME);
    }

    setSelectedVoiceover(voiceover);
  }, []);

  const removeVoiceover = useCallback(voiceoverId => {
    setVoiceovers(prevState => {
      const currentIndex = prevState.map(({ id }) => id).indexOf(voiceoverId);
      const filteredVoiceovers = prevState.filter(voiceover => voiceover.id !== voiceoverId);

      if (filteredVoiceovers[currentIndex]) {
        changeSelectedVoiceover(filteredVoiceovers[currentIndex]);
      } else if (filteredVoiceovers[0]) {
        changeSelectedVoiceover(filteredVoiceovers[0]);
      } else {
        changeSelectedVoiceover({});
      }

      return filteredVoiceovers;
    });
  }, []);

  const updateVoiceoverSeekValue = useCallback(state => {
    if (typeof state === 'number') setVoiceoverSeekValue(state);
  }, []);

  const changeIsNewVoiceoverSeekValue = useCallback(value => {
    setIsNewVoiceoverSeekValue(value);
  }, []);

  const voiceoversChanged = useCallback(value => {
    setIsVoiceoversChanged(value);
  }, []);

  const changeIsAudioPermissionChecked = useCallback(value => {
    setIsAudioPermissionChecked(value);
  }, []);

  const changeIsAudioPermissionAllowed = useCallback(value => {
    setIsAudioPermissionAllowed(value);
  }, []);

  const changeIsVoiceoverRecording = useCallback(value => {
    setIsVoiceoverRecording(value);
  }, []);

  const changeIsVoiceoverCountdown = useCallback(value => {
    setIsVoiceoverCountdown(value);
  }, []);

  const changeVoiceoverVolume = useCallback(value => {
    setVoiceoverVolume(value);
  }, []);

  const updateGeneralVoiceoverId = useCallback(value => {
    setGeneralVoiceoverId(value);
  }, []);

  const changeVoiceoversLoading = useCallback(value => {
    setIsVoiceoversLoading(value);
  }, []);

  const updatedStoryScenes = (story = null) => {
    if (!story) {
      setVideoParts([]);
      return;
    }
    const scenesWithDuration = story.scenes.sort((a, b) => a.sort - b.sort).map(({ trimmedDuration, name, id, playbackRate = 1, music = null, videoVolume }) => ({ trimmedDuration, playbackRate, name, id, music, videoVolume }));
    if (story?.introMeta?.duration) {
      scenesWithDuration.splice(0, 0, { trimmedDuration: story.introMeta.duration, name: 'Intro', id: 'intro' });
    }
    if (story?.outroMeta?.duration) {
      scenesWithDuration.push({ trimmedDuration: story.outroMeta.duration, name: 'Outro', id: 'outro' });
    }
    const scenesWithStartTimestamp = scenesWithDuration.map((item, index, array) => ({ ...item, start: index === 0 ? 0 : array.slice(0, index).map(({ trimmedDuration, playbackRate = 1 }) => trimmedDuration / playbackRate).reduce((i, j) => i + j) }));
    const scenesWithStartPoint = scenesWithStartTimestamp.map(item => ({ ...item, startPosition: roundToDigit(item.start / story.duration, 2) }));
    setVideoParts(scenesWithStartPoint);
  }

  const currentPart = useMemo(() => {
    const roundedMs = Math.round(videoProgress.milliseconds);
    return videoParts.find((item) => (roundedMs >= item.start) && (roundedMs < (item.start + item.trimmedDuration))) ?? null;
  }, [videoParts, videoProgress?.milliseconds]);

  const handleUpdateVolumes = useCallback((sceneId, volume, type) => {
    setVideoParts(prevState => prevState.map(item => {
      if (item.id === sceneId) {
        if (!item.hasOwnProperty('initialValues')) {
          item.initialValues = {
            videoVolume: item.videoVolume,
            musicVolume: item.music?.volume
          };
        }
        const changes = type === 'video'
          ? { videoVolume: volume }
          : { music: { ...item.music, volume } };
        return ({ ...item, ...changes });
      }
      return item;
    }));
  }, []);

  const resetVolumesInitialValues = useCallback(() => {
    setVideoParts(prevState => prevState.map(item => {
      if (item.hasOwnProperty('initialValues')) {
        delete item.initialValues;
      }
      return item;
    }));
  }, []);

  const resetVoiceovers = useCallback(() => {
    setVoiceovers([]);
    setInitialVoiceovers([]);
    setVoiceoverVolume(INITIAL_VOICEOVER_VOLUME);
    changeIsVoiceoverRecording(false);
    changeIsVoiceoverCountdown(false);
  }, []);

  return useMemo(
    () => ({
      isVoiceoverOpened,
      voiceovers,
      initialVoiceovers,
      voiceoverSeekValue,
      isNewVoiceoverSeekValue,
      selectedVoiceover,
      isVoiceoversChanged,
      isAudioPermissionChecked,
      isAudioPermissionAllowed,
      isVoiceoverRecording,
      isVoiceoverCountdown,
      voiceoverVolume,
      generalVoiceoverId,
      isVoiceoversLoading,
      videoParts,
      currentPart,
      setVoiceovers,
      openVoiceover,
      closeVoiceover,
      changeVoiceovers,
      updateVoiceovers,
      updateInitialVoiceovers,
      updateVoiceoverSeekValue,
      changeIsNewVoiceoverSeekValue,
      changeSelectedVoiceover,
      removeVoiceover,
      voiceoversChanged,
      resetVoiceovers,
      changeIsAudioPermissionChecked,
      changeIsAudioPermissionAllowed,
      changeIsVoiceoverRecording,
      changeIsVoiceoverCountdown,
      changeVoiceoverVolume,
      updateGeneralVoiceoverId,
      changeVoiceoversLoading,
      updatedStoryScenes,
      handleUpdateVolumes,
      resetVolumesInitialValues,
    }),
    [
      isVoiceoverOpened,
      voiceovers,
      initialVoiceovers,
      voiceoverSeekValue,
      isNewVoiceoverSeekValue,
      selectedVoiceover,
      isVoiceoversChanged,
      isAudioPermissionChecked,
      isAudioPermissionAllowed,
      isVoiceoverRecording,
      isVoiceoverCountdown,
      voiceoverVolume,
      generalVoiceoverId,
      isVoiceoversLoading,
      videoParts,
      currentPart,
      openVoiceover,
      closeVoiceover,
      changeVoiceovers,
      updateVoiceovers,
      updateInitialVoiceovers,
      updateVoiceoverSeekValue,
      changeIsNewVoiceoverSeekValue,
      changeSelectedVoiceover,
      removeVoiceover,
      voiceoversChanged,
      resetVoiceovers,
      changeIsAudioPermissionChecked,
      changeIsAudioPermissionAllowed,
      changeIsVoiceoverRecording,
      changeIsVoiceoverCountdown,
      changeVoiceoverVolume,
      updateGeneralVoiceoverId,
      changeVoiceoversLoading,
    ]
  );
}

export const useVoiceoverContext = constate(useVoiceover);

const VoicoverContext = {
  Provider: useVoiceoverContext.Provider,
};

export default VoicoverContext;
